import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { UserService } from '../user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { forkJoin, Subject, takeUntil } from 'rxjs';
import { IContactInfo } from '../models/contact.interface';
import { MatDialog } from '@angular/material/dialog';
import { ContactDeleteComponent } from '../contacts/contact-delete/contact-delete.component';
import { DialogSize } from '@wcg/frontend-framework';
import { ContactManageComponent } from '../contacts/contact-manage/contact-manage.component';
import { IrisService } from '@shared/services/iris.service';
import { selectUser } from '@shared/store/selectors/user.selectors';
import { Store } from '@ngrx/store';
import {
  UserChatData,
  generateChatKey,
} from '@data-access/talk-desk/types/talkdesk.type';
import { TALKDESK_ACTIONS } from '@data-access/talk-desk';

@Component({
  selector: 'wcg-profile',
  standalone: false,
  styleUrls: ['./profile.component.css'],
  templateUrl: './profile.component.html',
})
export class ProfileComponent implements OnInit, OnDestroy {
  // TODO: Check with Designer; on whether we need this anymore
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _userService = inject(UserService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _dialog = inject(MatDialog);
  private readonly _irisService = inject(IrisService);
  private readonly _store = inject(Store<any>);

  userInitials$ = this._userService.userInitials$;
  userDetails: any;
  profileForm!: FormGroup;
  contactInfo: any;

  emailAddresses: IContactInfo = {
    name: 'Email Addresses',
    type: 'emailAddresses',
    isAddBtn: true,
    listOperation: { edit: true, delete: true },
    listItems: [],
  };

  mailingAddresses: IContactInfo = {
    name: 'Physical Addresses',
    type: 'mailingAddresses',
    isAddBtn: true,
    listOperation: { edit: true, delete: true },
    listItems: [],
  };

  phoneNumbers: IContactInfo = {
    isAddBtn: true,
    isPhoneNumber: true,
    listItems: [],
    listOperation: {
      delete: true,
      edit: true,
    },
    name: 'Phone Numbers',
    type: 'phoneNumbers',
  };

  studyFacilities: IContactInfo = {
    isAddBtn: false,
    listItems: [],
    listOperation: {},
    name: 'Study Facilities',
    type: 'studyFacilities',
  };

  specializations: IContactInfo = {
    name: 'Specializations',
    type: 'specializations',
    isAddBtn: true,
    listOperation: { delete: true },
    listItems: [],
  };

  companies: IContactInfo = {
    isAddBtn: true,
    listItems: [],
    listOperation: { delete: true },
    name: 'Companies',
    type: 'companies',
  };

  degrees: IContactInfo = {
    isAddBtn: true,
    listItems: [],
    listOperation: { delete: true, arrow_up: true, arrow_down: true },
    name: 'Degrees',
    type: 'degrees',
  };

  degreesList: any[] = [];
  specialtiesList: any[] = [];
  companyList: any[] = [];
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.profileForm = this._formBuilder.group({
      user_id: new FormControl({ value: '', disabled: true }),
      npi: new FormControl({ value: '', disabled: true }),
    });

    this._store.select(selectUser).subscribe((userDetails: any) => {
      this.userDetails = userDetails;
      this.profileForm.patchValue({
        user_id: this.userDetails?.email,
        npi: this.userDetails?.npi,
      });
      if (this.isExternal()) {
        this.getContactInfo();
        this.getAllDegreesAndSpecialties();
      }
    });
  }

  getAllDegreesAndSpecialties() {
    const $degrees = this._irisService.getDegreeList();
    const $specialties = this._irisService.getSpecialtyList();
    const $companies = this._irisService.getAllOrganizations();
    forkJoin($degrees, $specialties, $companies)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        ([degreesList, specialtiesList, companyList]: [any, any, any]) => {
          if (degreesList && specialtiesList && companyList) {
            this.degreesList = degreesList.map((item: any) => {
              return { id: item, name: item };
            });
            this.specialtiesList = specialtiesList.map((item: any) => {
              return { id: item, name: item };
            });
            this.companyList = companyList.data.map((item: any) => {
              return { id: item['id'], name: item['name'] };
            });
          }
        },
      );
  }

  isExternal(): boolean {
    return !!(this.userDetails?.is_internal === false);
  }

  getContactInfo() {
    this._userService.getUserContactInfo().subscribe((contactInfo: any) => {
      if (contactInfo) {
        this.contactInfo = {
          companiesList: contactInfo.userOrganization || [],
          contactId: contactInfo.contactId,
          degreesList: contactInfo.degrees || [],
          emailAddressesList: contactInfo.emails || [],
          mailingAddressesList: contactInfo.mailingAddresses || [],
          phoneNumbersList: contactInfo.phones || [],
          siteFacilitiesList: contactInfo.siteFacilities || [],
          specializationsList: contactInfo.specialty || [],
        };
        this.emailAddresses['listItems'] =
          this.contactInfo.emailAddressesList.map(
            (item: any, index: number) => {
              return {
                id: index + 1,
                value: item,
                isDefault: false,
                listOperationAllowed: !(item == this.userDetails.email),
              };
            },
          );
        this.mailingAddresses['listItems'] =
          this.contactInfo.mailingAddressesList.map((item: any) => {
            return {
              id: item['id'],
              value:
                `${item.name ? item.name + ',' : ''} ${item.address1 ? item.address1 + ',' : ''} ${item.address2 ? item.address2 + ',' : ''} ${item.address3 ? item.address3 + ',' : ''} ${item.country?.name ? item.country?.name + ',' : ''} ${item.state ? item.state + ',' : ''} ${item.city ? item.city + ',' : ''} ${item.zip_code ?? ''}`.replace(
                  /\s+/g,
                  ' ',
                ),
              isDefault: false,
              listOperationAllowed: true,
            };
          });
        this.phoneNumbers['listItems'] = this.contactInfo.phoneNumbersList.map(
          (item: any, index: number) => {
            return {
              id: index + 1,
              value: item,
              isDefault: false,
              listOperationAllowed: true,
            };
          },
        );
        this.studyFacilities['listItems'] =
          this.contactInfo.studyFacilitiesList.map((item: any) => {
            return {
              id: item['id'],
              value:
                `${item.name ? item.name + ',' : ''} ${item.address1 ? item.address1 + ',' : ''} ${item.address2 ? item.address2 + ',' : ''} ${item.address3 ? item.address3 + ',' : ''} ${item.country?.name ? item.country?.name + ',' : ''} ${item.state ? item.state + ',' : ''} ${item.city ? item.city + ',' : ''} ${item.zip_code ?? ''}`.replace(
                  /\s+/g,
                  ' ',
                ),
              isDefault: false,
              listOperationAllowed: true,
            };
          });
        this.specializations['listItems'] =
          this.contactInfo.specializationsList.map(
            (item: any, index: number) => {
              return {
                id: index + 1,
                value: item,
                isDefault: false,
                listOperationAllowed: true,
              };
            },
          );
        this.companies['listItems'] = this.contactInfo.companiesList.map(
          (item: any) => {
            return {
              id: item.organization.id,
              value: item.organization.name,
              isDefault: false,
              listOperationAllowed: true,
            };
          },
        );
        this.degrees['listItems'] = this.contactInfo.degreesList.map(
          (item: any, index: number) => {
            return {
              id: index + 1,
              value: item,
              isDefault: false,
              listOperationAllowed: true,
              order: index + 1,
            };
          },
        );
      }
    });
  }

  emitContactEvent(event: any) {
    if (event) {
      if (event[3] == 'add') {
        const eventType = event[2];
        switch (eventType) {
          case 'emailAddresses':
            this.openContactAddDialog(
              eventType,
              this.emailAddresses['listItems'],
              'email address',
              'Email',
              'email',
              [],
              false,
            );
            break;
          case 'mailingAddresses':
            this.openContactAddDialog(
              eventType,
              this.mailingAddresses['listItems'],
              'mailing address',
              'Mailing address',
              'mailing-address-json',
              [],
              false,
            );
            break;
          case 'phoneNumbers':
            this.openContactAddDialog(
              eventType,
              this.phoneNumbers['listItems'],
              'phone number',
              'Phone number',
              'number',
              [],
              false,
            );
            break;
          case 'specializations':
            this.openContactAddDialog(
              eventType,
              this.specializations['listItems'],
              'specialization',
              'Specialization',
              'dropdown',
              this.specialtiesList,
              false,
            );
            break;
          case 'companies':
            this.openContactAddDialog(
              eventType,
              this.companies['listItems'],
              'company',
              'Company',
              'dropdown',
              this.companyList,
              false,
            );
            break;
          case 'degrees':
            this.openContactAddDialog(
              eventType,
              this.degrees['listItems'],
              'degree',
              'Degree',
              'dropdown',
              this.degreesList,
              false,
            );
            break;
          default:
            console.error('Unknown action type');
            break;
        }
      } else if (event[3] == 'edit') {
        const eventType = event[2];
        switch (eventType) {
          case 'emailAddresses':
            this.openContactEditDialog(
              eventType,
              event[0],
              this.emailAddresses['listItems'],
              'email address',
              'Email',
              'email',
              [],
              false,
            );
            break;
          case 'mailingAddresses':
            this.openContactEditDialog(
              eventType,
              event[0],
              this.mailingAddresses['listItems'],
              'mailing address',
              'Mailing address',
              'mailing-address-json',
              [],
              false,
            );
            break;
          case 'phoneNumbers':
            this.openContactEditDialog(
              eventType,
              event[0],
              this.phoneNumbers['listItems'],
              'phone number',
              'Phone number',
              'number',
              [],
              false,
            );
            break;
          default:
            console.log('Unknown action type');
            break;
        }
      } else if (event[3] == 'delete') {
        const eventType = event[2];
        switch (eventType) {
          case 'emailAddresses':
            this.openContactDeleteDialog(
              event,
              this.emailAddresses['listItems'],
              'email address',
            );
            break;
          case 'mailingAddresses':
            this.openContactDeleteDialog(
              event,
              this.mailingAddresses['listItems'],
              'mailing address',
            );
            break;
          case 'phoneNumbers':
            this.openContactDeleteDialog(
              event,
              this.phoneNumbers['listItems'],
              'phone number',
            );
            break;
          case 'specializations':
            this.openContactDeleteDialog(
              event,
              this.specializations['listItems'],
              'specialization',
            );
            break;
          case 'companies':
            this.openContactDeleteDialog(
              event,
              this.companies['listItems'],
              'company',
            );
            break;
          case 'degrees':
            this.openContactDeleteDialog(
              event,
              this.degrees['listItems'],
              'degree',
            );
            break;
          default:
            console.error('Unknown action type');
            break;
        }
      } else if (event[3] == 'arrow_up' || event[3] == 'arrow_down') {
        const eventType = event[2];
        switch (eventType) {
          case 'degrees':
            this.sortListByOrder(event, this.degrees['listItems']);
            break;
          default:
            console.error('Unknown action type');
            break;
        }
      }
    }
  }

  openContactAddDialog(
    eventType: any,
    dataList: any,
    type: string,
    inputLable: string,
    inputType: string,
    dropDownList: any,
    isDefaultVisible: boolean,
  ) {
    const dialogRef = this._dialog.open(ContactManageComponent, {
      data: {
        title: `Add ${type}`,
        buttonTitle: 'Add',
        dialogSize:
          inputType == 'mailing-address-json'
            ? DialogSize.MEDIUM
            : DialogSize.SMALL,
        inputLable: inputLable,
        inputType: inputType,
        dropDownList: dropDownList,
        btnEvent: 'add',
        isDefaultVisible: isDefaultVisible,
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result?.event !== 'add') return;

      if (eventType === 'mailingAddresses') {
        const existingAddress = this.contactInfo.mailingAddressesList.find(
          (item: any) => item.name === result?.value?.name,
        );
        if (existingAddress) {
          this._snackBar.open(
            `${result?.value?.name} address already exists!`,
            'Error',
            { duration: 2000 },
          );
          return;
        }
        this.addMailingAddressContactInfo(result?.value);
      } else if (eventType === 'companies') {
        const existingValue = dataList.find(
          (item: any) => item.id === result?.value,
        );
        if (existingValue) {
          this._snackBar.open(
            `${existingValue?.value} ${type} already exists!`,
            'Error',
            { duration: 2000 },
          );
          return;
        }
        this.addCompanyContactInfo(result?.value);
      } else {
        const existingValue = dataList.find(
          (item: any) => item.value === result?.value,
        );
        if (existingValue) {
          this._snackBar.open(
            `${result?.value} ${type} already exists!`,
            'Error',
            { duration: 2000 },
          );
          return;
        }

        const eventData: Record<string, string> = {
          emailAddresses: 'emails',
          phoneNumbers: 'phones',
          specializations: 'specialty',
          degrees: 'degrees',
        };

        const eventTypeKey = eventData[eventType];
        const userDetails: Record<string, any> = {
          contact_id: this.contactInfo.contact_id,
          [eventTypeKey]: [
            ...dataList.map((data: any) => data.value),
            result?.value,
          ].filter(Boolean),
        };

        this.updateUserContactInfo(userDetails);
      }
    });
  }
  updateUserContactInfo(userDetails: any) {
    this._userService.updateUserContactInfo(userDetails).subscribe((res) => {
      if (res) this.getContactInfo();
    });
  }
  addMailingAddressContactInfo(address: any) {
    this._userService
      .addUserMailingAddress(this.contactInfo.contact_id, address)
      .subscribe((res) => {
        if (res) this.getContactInfo();
      });
  }
  addCompanyContactInfo(organization_id: any) {
    this._userService.addOrganization(organization_id).subscribe((res) => {
      if (res) this.getContactInfo();
    });
  }

  openContactEditDialog(
    eventType: any,
    rowData: any,
    dataList: any,
    type: string,
    inputLable: string,
    inputType: string,
    dropDownList: any,
    isDefaultVisible: boolean,
  ) {
    if (rowData.isDefault) {
      isDefaultVisible = false;
    }
    if (inputType == 'mailing-address-json') {
      rowData = this.contactInfo.mailingAddressesList.find(
        (item: any) => item.id == rowData.id,
      );
    }
    const dialogRef = this._dialog.open(ContactManageComponent, {
      data: {
        title: `Update ${type}`,
        buttonTitle: 'Update',
        dialogSize:
          inputType == 'mailing-address-json'
            ? DialogSize.MEDIUM
            : DialogSize.SMALL,
        inputLable: inputLable,
        inputType: inputType,
        dropDownList: dropDownList,
        btnEvent: 'update',
        rowData: rowData,
        isDefaultVisible: isDefaultVisible,
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result?.event !== 'update') return;

      if (eventType === 'mailingAddresses') {
        const existingAddress = this.contactInfo.mailingAddressesList.find(
          (item: any) =>
            item.name === result?.value?.name && item.id != rowData.id,
        );
        if (existingAddress) {
          this._snackBar.open(
            `${result?.value?.name} address already exists!`,
            'Error',
            { duration: 2000 },
          );
          return;
        }
        this.updateUserMailingAddress(result?.value, rowData.id);
      } else {
        const existingValue = dataList.find(
          (item: any) => item.value === result?.value && item.id != rowData.id,
        );
        if (existingValue) {
          this._snackBar.open(
            `${result?.value} ${type} already exists!`,
            'Error',
            { duration: 2000 },
          );
          return;
        }

        const eventData: Record<string, string> = {
          emailAddresses: 'emails',
          phoneNumbers: 'phones',
        };
        const contactType = eventType as keyof ProfileComponent;
        const elemnet = this[contactType]?.listItems.find(
          (ele: any) => ele.id == rowData.id,
        );
        if (!elemnet) return;

        elemnet.value = result?.value;
        elemnet.isDefault = result?.isDefault;

        const eventTypeKey = eventData[eventType];
        const userDetails: Record<string, any> = {
          contact_id: this.contactInfo.contact_id,
          [eventTypeKey]: this[contactType]?.listItems.map(
            (data: any) => data.value,
          ),
        };

        this.updateUserContactInfo(userDetails);
      }
    });
  }

  updateUserMailingAddress(address: any, address_id: string) {
    this._userService
      .updateUserMailingAddress(
        this.contactInfo.contact_id,
        address,
        address_id,
      )
      .subscribe((res) => {
        if (res) this.getContactInfo();
      });
  }

  openContactDeleteDialog(event: any, dataList: any, type: string) {
    const rowData: any = event[0];
    const eventType: any = event[2];
    if (rowData.isDefault) {
      dataList = dataList.filter((item: any) => item.id != rowData.id);
    }
    const dialogRef = this._dialog.open(ContactDeleteComponent, {
      data: {
        title: `Remove ${type}`,
        message: `Are you sure you want to remove this ${type}?`,
        buttonTitle: rowData.isDefault ? 'Update and remove' : 'Remove',
        isDefault: rowData.isDefault,
        dataList: dataList,
        alertMessage: `${rowData.value} is default ${type}`,
        updateTitle: `Update default ${type}`,
        dialogSize: DialogSize.SMALL,
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      // Check if the event is a delete event and the button event is either remove or update_and_remove
      if (
        result?.event === 'delete' &&
        (result?.btnEvent === 'remove' ||
          result?.btnEvent === 'update_and_remove')
      ) {
        if (eventType === 'mailingAddresses') {
          this.removeMailingAddressContactInfo(rowData.id);
        } else if (eventType === 'companies') {
          const user_organization_id = this.contactInfo.companiesList.find(
            (item: any) => item.organization.id == rowData.id,
          ).id;
          this.removeCompanyContactInfo(user_organization_id);
        } else {
          const eventData: Record<string, string> = {
            emailAddresses: 'emails',
            phoneNumbers: 'phones',
            specializations: 'specialty',
            degrees: 'degrees',
          };
          const eventTypeKey = eventData[eventType];
          const contactType = eventType as keyof ProfileComponent;
          const filteredItems = this[contactType].listItems.filter(
            (item: any) => item.id !== rowData.id,
          );
          const updatedValues = filteredItems.map((data: any) => data.value);
          const userDetails: any = {
            contact_id: this.contactInfo.contact_id,
            [eventTypeKey]: updatedValues,
          };
          this.updateUserContactInfo(userDetails);
        }
      }
    });
  }

  removeMailingAddressContactInfo(address_id: string) {
    this._userService
      .removeUserMailingAddress(this.contactInfo.contact_id, address_id)
      .subscribe((res) => {
        if (res) this.getContactInfo();
      });
  }
  removeCompanyContactInfo(user_organization_id: string) {
    this._userService
      .removeOrganization(user_organization_id)
      .subscribe((res) => {
        if (res) this.getContactInfo();
      });
  }

  sortListByOrder(event: any, dataList: any) {
    const rowData: any = event[0];
    const eventType: any = event[2];
    const eventName: any = event[3];
    const contactType: keyof ProfileComponent = eventType;
    if (eventName == 'arrow_up') {
      this.moveItemUp(contactType, rowData.id, dataList);
      return;
    }
    if (eventName == 'arrow_down') {
      this.moveItemDown(contactType, rowData.id, dataList);
      return;
    }
  }

  moveItemUp(contactType: keyof ProfileComponent, itemId: any, dataList: any) {
    const itemIndex = dataList.findIndex((item: any) => item.id === itemId);
    if (itemIndex > 0) {
      const tempOrder = dataList[itemIndex].order;
      dataList[itemIndex].order = dataList[itemIndex - 1].order;
      dataList[itemIndex - 1].order = tempOrder;
      dataList.sort((a: any, b: any) => a.order - b.order);
      this[contactType].listItems = dataList;
      const userDetails: Record<string, any> = {
        contact_id: this.contactInfo.contact_id,
        degrees: dataList.map((data: any) => data.value),
      };
      this.updateUserContactInfo(userDetails);
    }
  }

  moveItemDown(
    contactType: keyof ProfileComponent,
    itemId: any,
    dataList: any,
  ) {
    const itemIndex = dataList.findIndex((item: any) => item.id === itemId);
    if (itemIndex < dataList.length - 1) {
      const tempOrder = dataList[itemIndex].order;
      dataList[itemIndex].order = dataList[itemIndex + 1].order;
      dataList[itemIndex + 1].order = tempOrder;
      dataList.sort((a: any, b: any) => a.order - b.order);
      this[contactType].listItems = dataList;
      const userDetails: Record<string, any> = {
        contact_id: this.contactInfo.contact_id,
        degrees: dataList.map((data: any) => data.value),
      };
      this.updateUserContactInfo(userDetails);
    }
  }

  contactClientCare() {
    const chatData: UserChatData = {
      contextType: 'user',
      isOpen: true,
      primaryId: this.userDetails.id,
      userId: this.userDetails.id,
      userName: `${this.userDetails?.first_name} ${this.userDetails?.last_name}`,
      user: this.userDetails,
      question: 'How can I update my name or email?',
    };

    const chatId = generateChatKey('user', this.userDetails.id);
    this._store.dispatch(TALKDESK_ACTIONS.openChat({ chatId, chatData }));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
